/**
 * Pure H OS · Sprint 2 · Platform Services state.
 *
 * Owns the shared state that spans every future module:
 *   - Command palette open/close
 *   - Notification drawer open/close
 *   - Notifications list (with unread flags)
 *   - User preferences
 *
 * Sprint 12 upgrade: `NOTIFICATIONS` and `DEFAULT_PREFERENCES` are now
 * live views over MongoDB. The provider composes on-the-fly with a
 * persisted read-map so notification state survives reloads without
 * capturing stale mock data at mount time.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import {
  NOTIFICATIONS, Notification,
  DEFAULT_PREFERENCES, Preferences, Language, Density,
} from "../mocks/platformData";

const PREFS_KEY = "pureh:prefs:v1";
const NOTIF_KEY = "pureh:notifs:v1";

type PlatformContextValue = {
  // Palette
  paletteOpen: boolean;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;

  // Notifications
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;

  // Shortcuts help
  shortcutsOpen: boolean;
  openShortcuts: () => void;
  closeShortcuts: () => void;

  // Preferences
  prefs: Preferences;
  setLanguage: (l: Language) => void;
  setDensity: (d: Density) => void;
  setNotifPref: (k: keyof Preferences["notifications"], v: boolean) => void;
};

const PlatformContext = createContext<PlatformContextValue | undefined>(undefined);

function loadReadMap(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(NOTIF_KEY);
    if (raw) return JSON.parse(raw) as Record<string, boolean>;
  } catch { /* empty */ }
  return {};
}

function persistReadMap(map: Record<string, boolean>) {
  try { localStorage.setItem(NOTIF_KEY, JSON.stringify(map)); } catch { /* empty */ }
}

function loadPrefsPartial(): Partial<Preferences> {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw) as Partial<Preferences>;
  } catch { /* empty */ }
  return {};
}

export function PlatformProvider({ children }: { children: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [readMap, setReadMap] = useState<Record<string, boolean>>(loadReadMap);
  const [prefsOverride, setPrefsOverride] = useState<Partial<Preferences>>(loadPrefsPartial);

  // Compose live preferences with local overrides so user tweaks survive
  // reloads without freezing at first render.
  const prefs = useMemo<Preferences>(() => ({
    language:   prefsOverride.language   ?? DEFAULT_PREFERENCES.language,
    density:    prefsOverride.density    ?? DEFAULT_PREFERENCES.density,
    notifications: { ...DEFAULT_PREFERENCES.notifications, ...(prefsOverride.notifications ?? {}) },
  }), [prefsOverride]);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefsOverride)); }
    catch { /* empty */ }
  }, [prefsOverride]);

  // Notifications are derived from the LIVE base list + local read map.
  const notifications = useMemo<Notification[]>(
    () => NOTIFICATIONS.map((n) => ({ ...n, read: readMap[n.id] ?? n.read })),
    [readMap, NOTIFICATIONS.length],
  );

  const markRead = useCallback((id: string) => {
    setReadMap((m) => {
      const next = { ...m, [id]: true };
      persistReadMap(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadMap(() => {
      const next: Record<string, boolean> = {};
      for (const n of NOTIFICATIONS) next[n.id] = true;
      persistReadMap(next);
      return next;
    });
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  const value: PlatformContextValue = {
    paletteOpen,
    openPalette:   () => setPaletteOpen(true),
    closePalette:  () => setPaletteOpen(false),
    togglePalette: () => setPaletteOpen((v) => !v),

    drawerOpen,
    openDrawer:   () => setDrawerOpen(true),
    closeDrawer:  () => setDrawerOpen(false),
    toggleDrawer: () => setDrawerOpen((v) => !v),

    notifications,
    unreadCount,
    markRead,
    markAllRead,

    shortcutsOpen,
    openShortcuts:  () => setShortcutsOpen(true),
    closeShortcuts: () => setShortcutsOpen(false),

    prefs,
    setLanguage: (l) => setPrefsOverride((p) => ({ ...p, language: l })),
    setDensity:  (d) => setPrefsOverride((p) => ({ ...p, density: d })),
    setNotifPref: (k, v) =>
      setPrefsOverride((p) => ({
        ...p,
        notifications: { ...(p.notifications ?? DEFAULT_PREFERENCES.notifications), [k]: v },
      })),
  };

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error("usePlatform must be used inside PlatformProvider");
  return ctx;
}
